import type { ReactNode } from "react";
import { useSiteContent } from "@/lib/siteContent";
import { formatBRL } from "@/lib/format";
import type { CartItem, CheckoutForm, ShippingResult } from "@/types";

interface StepConferenciaProps {
  form: CheckoutForm;
  items: CartItem[];
  subtotal: number;
  discount: number;
  shipping: ShippingResult;
  total: number;
  onEdit: (step: number) => void;
}

function Section({
  title,
  step,
  onEdit,
  children,
}: {
  title: string;
  step: number;
  onEdit: (step: number) => void;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading text-lg text-foreground-900">{title}</h3>
        {step >= 0 ? (
          <button
            type="button"
            onClick={() => onEdit(step)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-primary-700 underline underline-offset-4"
          >
            <i className="ri-pencil-line text-sm" />
            Editar
          </button>
        ) : null}
      </div>
      <div className="mt-3 text-sm text-foreground-700">{children}</div>
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-0.5">
      <span className="text-foreground-500">{label}</span>
      <span className="text-right text-foreground-800">{value}</span>
    </div>
  );
}

export default function StepConferencia({
  form,
  items,
  subtotal,
  discount,
  shipping,
  total,
  onEdit,
}: StepConferenciaProps) {
  const content = useSiteContent();
  const method = form.deliveryMethod
    ? content.delivery.find((item) => item.id === form.deliveryMethod)
    : undefined;
  const showAddress = Boolean(method && method.id !== "retirada" && method.id !== "onibus");

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-heading text-2xl text-foreground-900">Confira seu pedido</h2>
        <p className="mt-1 text-sm text-foreground-600">
          Dê uma olhada com calma. Se algo precisar mudar, é só editar antes de enviar.
        </p>
      </div>

      <Section title="Seus dados" step={0} onEdit={onEdit}>
        <Line label="Nome" value={form.name || "—"} />
        <Line label="WhatsApp" value={form.phone || "—"} />
        {form.email.trim() ? <Line label="E-mail" value={form.email} /> : null}
        <Line label="Cidade" value={`${form.city || "—"} — ${form.state || "—"}`} />
        <Line label="Consentimento" value={form.consent ? "Autorizado" : "Pendente"} />
      </Section>

      <Section title="Produtos escolhidos" step={-1} onEdit={onEdit}>
        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <div key={item.key} className="flex items-center gap-3 border-b border-background-200 pb-3 last:border-0 last:pb-0">
              <div className="h-14 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={item.image}
                  alt={`${item.name} na cor ${item.colorName}`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground-900">{item.name}</p>
                <p className="text-[11px] text-foreground-500">
                  {item.code} · {item.colorName} · Tam. {item.size} · {item.quantity}x
                </p>
              </div>
              <span className="whitespace-nowrap text-sm text-foreground-800">
                {formatBRL(item.unitPrice * item.quantity)}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Recebimento" step={2} onEdit={onEdit}>
        <Line label="Modalidade" value={method?.name ?? "—"} />
        <Line label="Prazo apresentado" value={method?.deadline ?? "—"} />
        {form.deliveryMethod === "onibus" ? (
          <>
            <Line
              label="Destino"
              value={`${form.bus.destinationCity || "—"} — ${form.bus.destinationState || "—"}`}
            />
            {form.bus.terminal ? <Line label="Ponto de retirada" value={form.bus.terminal} /> : null}
            {form.bus.responsibleName ? (
              <Line label="Responsável" value={`${form.bus.responsibleName} · ${form.bus.responsiblePhone}`} />
            ) : null}
          </>
        ) : null}
      </Section>

      {showAddress ? (
        <Section title="Endereço" step={1} onEdit={onEdit}>
          <Line label="Rua" value={`${form.address.street || "—"}, ${form.address.number || "—"}`} />
          {form.address.complement ? <Line label="Complemento" value={form.address.complement} /> : null}
          <Line label="Bairro" value={form.address.district || "—"} />
          <Line
            label="Cidade"
            value={`${form.address.city || form.city || "—"} — ${form.address.state || form.state || "—"}`}
          />
          {form.address.cep ? <Line label="CEP" value={form.address.cep} /> : null}
          {form.address.reference ? <Line label="Referência" value={form.address.reference} /> : null}
        </Section>
      ) : null}

      <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <h3 className="font-heading text-lg text-foreground-900">Resumo</h3>
        <div className="mt-3 flex flex-col gap-1.5 text-sm text-foreground-700">
          <Line label="Quantidade de itens" value={String(items.reduce((s, i) => s + i.quantity, 0))} />
          <Line label="Produtos" value={formatBRL(subtotal)} />
          <Line label="Desconto" value={discount > 0 ? `-${formatBRL(discount)}` : formatBRL(0)} />
          <Line
            label="Frete"
            value={
              shipping.fee === null ? "Taxa a confirmar" : shipping.freeApplied ? "Grátis" : formatBRL(shipping.fee)
            }
          />
          <div className="mt-2 flex items-center justify-between border-t border-background-300 pt-3">
            <span className="font-heading text-xl text-foreground-900">Total estimado</span>
            <span className="font-heading text-xl text-primary-700">{formatBRL(total)}</span>
          </div>
        </div>
      </div>

      <p className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-4 text-xs leading-relaxed text-foreground-700">
        <i className="ri-whatsapp-line mt-0.5 text-base text-secondary-700" />
        Ao confirmar, você abre o WhatsApp com o pedido completo. A disponibilidade, o prazo, o frete
        e a forma de pagamento serão confirmados no atendimento.
      </p>
    </div>
  );
}